import React from "react";

/* Opportunity detail — slide-in panel from the right.
   All copy on this panel comes from the LLM judge layer (headline, why,
   matched_customer, use_case, pain_point, intent_type, suggested_angle,
   caution). The raw Reddit columns (title, body, permalink, score, comments,
   created_at) only fill in the "Original post" + meta strip.

   Two render modes:
     - Solo opp (post or single comment)   → single-pane body
     - Comment cluster (kind === "comment-cluster") → thread header followed
       by one section per clustered comment, each with its own metadata grid,
       per-section feedback, and "Open comment on Reddit" link. */

const FB_REASONS = [
  { id: "relevant", label: "Good match", icon: "ThumbsUp" },
  { id: "not_relevant", label: "Not relevant", icon: "ThumbsDown" },
  { id: "wrong_customer", label: "Wrong customer", icon: "User" },
  { id: "too_broad", label: "Too broad", icon: "Layers" },
  { id: "already_seen", label: "Already seen", icon: "Eye" },
];

function tierClassOf(tier) {
  if (tier === "high") return "tier-high";
  if (tier === "medium") return "tier-med";
  return "tier-low";
}
function tierLabelOf(tier) {
  if (tier === "high") return "HIGH INTENT";
  if (tier === "medium") return "MEDIUM INTENT";
  return "LOW INTENT";
}

// Normalize a permalink (relative or absolute) to a clickable URL.
function permalinkUrl(raw) {
  if (!raw) return null;
  return raw.startsWith("http")
    ? raw
    : `https://www.reddit.com${raw.startsWith("/") ? "" : "/"}${raw}`;
}

// Post permalink, or comment permalink stripped to the thread root.
function threadPermalinkUrl(raw) {
  const url = permalinkUrl(raw);
  if (!url) return null;
  const m = url.match(/^(.*?\/comments\/[a-z0-9]+\/[^/]+\/)/i);
  return m ? m[1] : url;
}

// Parse the post / comment id out of a Reddit permalink (mirrors the helpers
// in api.jsx — kept local so the panel doesn't depend on them).
function parsePostId(permalink) {
  const m = (permalink || "").match(/\/comments\/([a-z0-9]+)(?:\/|$)/i);
  return m ? m[1] : null;
}
function parseCommentId(permalink) {
  const m = (permalink || "").match(/\/comments\/[a-z0-9]+\/[^/]+\/([a-z0-9]+)/i);
  return m ? m[1] : null;
}

// The post/comment author to DM, normalized (no "u/" prefix). null when the
// opportunity has no known author (e.g. deleted accounts) — PM is unavailable.
function recipientFor(o) {
  const name = String(o?.authorName || "").trim().replace(/^\/?u\//i, "").trim();
  return name || null;
}

const PM_SUBJECT_MAX = 100;

// Seed a PM subject from the thread/post title: "Re: <title>", clamped.
function defaultSubject(o) {
  const base = String(o?.redditTitle || o?.title || "").trim();
  if (!base) return "Saw your post on Reddit";
  const subject = `Re: ${base}`;
  return subject.length > PM_SUBJECT_MAX
    ? `${subject.slice(0, PM_SUBJECT_MAX - 1).trimEnd()}…`
    : subject;
}

// The Reddit "thing" a reply should target for this opportunity:
//   comment → reply to that comment (t1_…); post → reply to the post (t3_…).
function parentThingIdFor(o) {
  if (!o) return null;
  if (o.kind === "comment") {
    const cid = o.redditCommentId || parseCommentId(o.permalink);
    if (cid) return `t1_${cid}`;
  }
  const pid = o.redditPostId || parsePostId(o.permalink);
  return pid ? `t3_${pid}` : null;
}

function DetailPanel({
  opp, isOpen, onClose, onFeedback, onDeleteOpportunity,
  activeProfileId, onNavigate, initialGenMode = null,
}) {
  // Keep last opp visible while sliding closed (avoid flash)
  const [cached, setCached] = React.useState(opp);
  // Which composer is open: null, "comment" (public reply), or "pm" (DM author).
  const [genMode, setGenMode] = React.useState(null);
  const bodyRef = React.useRef(null);
  React.useEffect(() => { if (opp) setCached(opp); }, [opp]);

  // Sync the composer to the opener's intent whenever the panel opens or the
  // opp changes: a "Generate …" feed button passes initialGenMode to jump
  // straight into that draft; a plain open closes any composer. Clusters have
  // no single reply target, so they always open with the composer closed.
  React.useEffect(() => {
    const wantsGen = initialGenMode && isOpen && opp && opp.kind !== "comment-cluster";
    setGenMode(wantsGen ? initialGenMode : null);
  }, [isOpen, opp?.id, initialGenMode]);

  React.useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = 0;
  }, [isOpen, opp?.id]);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && isOpen) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const o = cached;
  if (!o) return null;

  const isCluster = o.kind === "comment-cluster";

  return (
    <>
      <div className={"detail-overlay " + (isOpen ? "is-open" : "")} onClick={onClose} />
      <aside
        className={"detail-panel " + (isOpen ? "is-open" : "")}
        role="dialog"
        aria-label="Opportunity detail"
      >
        <div className="detail-header">
          <div className="detail-header-kicker">
            <span className={"opp-brief-tag " + tierClassOf(o.tier)}>
              {tierLabelOf(o.tier)}
            </span>
            <span className="detail-header-pct">{o.score}%</span>
            <span className="detail-header-pct-label">
              {isCluster ? "top match" : "match"}
            </span>
          </div>
          <div className="spacer" />
          <button className="btn btn-ghost btn-icon" onClick={onClose} title="Close (Esc)">
            <window.I.X size={16} />
          </button>
        </div>

        <div ref={bodyRef} className="detail-body scroll">
          {isCluster ? (
            <ClusterBody
              opp={o}
              onFeedback={onFeedback}
              onDeleteOpportunity={onDeleteOpportunity}
            />
          ) : (
            <SoloBody
              opp={o}
              onFeedback={onFeedback}
              onDeleteOpportunity={onDeleteOpportunity}
            />
          )}
        </div>

        <div className="detail-footer">
          {!isCluster && (
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              onClick={() => setGenMode("comment")}
              title="Draft a public Reddit reply for this opportunity"
            >
              <window.I.Sparkles size={14} />
              Generate Comment
            </button>
          )}
          {!isCluster && recipientFor(o) && (
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              onClick={() => setGenMode("pm")}
              title={`Draft a private message to u/${recipientFor(o)}`}
            >
              <window.I.Mail size={14} />
              Generate PM
            </button>
          )}
          <div style={{ flex: 1 }} />
          {(() => {
            const url = permalinkUrl(o.permalink);
            return (
              <a
                className="btn btn-primary btn-sm"
                href={url || "#"}
                target={url ? "_blank" : undefined}
                rel={url ? "noopener noreferrer" : undefined}
                onClick={(e) => { if (!url) e.preventDefault(); }}
                aria-disabled={!url}
                style={!url ? { opacity: 0.5, pointerEvents: "none" } : null}
                title={url ? "Open on reddit.com" : "No permalink for this item"}
              >
                <window.I.External size={14} />
                Open full thread on Reddit
              </a>
            );
          })()}
        </div>
      </aside>

      {genMode && (
        <GenerateResponseDialog
          mode={genMode}
          opp={o}
          profileId={activeProfileId || window.api?.storage?.readProfileId?.() || null}
          onNavigate={onNavigate}
          onClose={() => setGenMode(null)}
        />
      )}
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Generate Response dialog — overlays the detail panel. Checks Reddit       */
/* connection, generates an editable draft, and posts on explicit click.     */
/* ────────────────────────────────────────────────────────────────────── */

/* Built-in tone presets mirror REPLY_TONES on the backend. The `link` flag
   marks the tones that lean on the profile's product link — the dialog surfaces
   whether that link actually exists so the choice is informed. Users can layer
   their own custom tones on top (managed under Settings → Reddit). */
const BUILTIN_TONES = [
  { id: "helpful", label: "Helpful", icon: "ThumbsUp",
    hint: "Pure value — answers them, no product mention." },
  { id: "soft", label: "Soft mention", icon: "Sparkles", link: true,
    hint: "Helpful first, with one natural product mention." },
  { id: "expert", label: "Expert", icon: "GraduationCap",
    hint: "Authoritative, detailed practitioner voice." },
  { id: "cta", label: "Product CTA", icon: "Zap", link: true,
    hint: "Recommends your product with a Markdown link." },
];

// How a custom tone's product_mode renders in the chip row.
const TONE_MODE_META = {
  none: { icon: "ThumbsUp", link: false, hint: "No product mention." },
  soft: { icon: "Sparkles", link: true, hint: "Soft, disclosed product mention." },
  cta: { icon: "Zap", link: true, hint: "Recommends your product with a link." },
};

// Map a stored custom tone into the same shape the chip row uses for builtins.
function customToneEntry(t) {
  const meta = TONE_MODE_META[t.product_mode] || TONE_MODE_META.soft;
  return {
    id: t.id,
    label: t.name,
    icon: meta.icon,
    link: meta.link,
    custom: true,
    hint: (t.instructions && t.instructions.trim()) || meta.hint,
  };
}

// "https://signalleaf.ai/pricing?x=1" → "signalleaf.ai". Empty string if none.
function prettyHost(website) {
  const raw = String(website || "").trim();
  if (!raw || raw === "—" || raw === "No website added") return "";
  try {
    const u = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    return u.hostname.replace(/^www\./, "") + (u.pathname !== "/" ? u.pathname : "");
  } catch {
    return raw.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "");
  }
}

// Minimal inline Markdown → React nodes for the live preview. Covers the bits
// that actually show up in Reddit replies: links, bold, italic, inline code.
// Text is rendered as React children, so it's escaped — no HTML injection.
function renderInlineMd(text, kp) {
  const nodes = [];
  const re =
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|(?:\*|_)([^*_\n]+)(?:\*|_)|`([^`]+)`/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1]) {
      nodes.push(
        <a key={`${kp}-${i}`} href={m[2]} target="_blank" rel="noopener noreferrer"
          style={{ color: "var(--accent)", textDecoration: "underline" }}>
          {m[1]}
        </a>,
      );
    } else if (m[3]) {
      nodes.push(<strong key={`${kp}-${i}`}>{m[3]}</strong>);
    } else if (m[4]) {
      nodes.push(<em key={`${kp}-${i}`}>{m[4]}</em>);
    } else if (m[5]) {
      nodes.push(
        <code key={`${kp}-${i}`} style={{
          background: "var(--bg-elevated, var(--bg))",
          border: "1px solid var(--border-subtle)",
          borderRadius: 4, padding: "1px 5px", fontSize: "0.92em",
        }}>{m[5]}</code>,
      );
    }
    last = re.lastIndex;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function MarkdownPreview({ text }) {
  const trimmed = String(text || "").trim();
  if (!trimmed) {
    return (
      <span style={{ color: "var(--text-quaternary)" }}>
        Nothing to preview yet.
      </span>
    );
  }
  const paras = trimmed.split(/\n{2,}/);
  return paras.map((para, pi) => (
    <p key={pi} style={{ margin: pi === paras.length - 1 ? 0 : "0 0 10px" }}>
      {para.split("\n").map((line, li) => (
        <React.Fragment key={li}>
          {li > 0 && <br />}
          {renderInlineMd(line, `${pi}-${li}`)}
        </React.Fragment>
      ))}
    </p>
  ));
}

function GenerateResponseDialog({ mode = "comment", opp, profileId, onNavigate, onClose }) {
  // "pm" drafts a one-to-one private message to the author and sends it via
  // Reddit's compose API; "comment" (default) drafts a public reply and posts
  // it to the thread. The connect gate, tone selector, and editor are shared.
  const isPM = mode === "pm";
  const recipient = recipientFor(opp);
  const userId = window.api?.auth?.readUserId?.() || null;
  // connection: null = checking; { connected: bool, reddit_username }
  const [connection, setConnection] = React.useState(null);
  const [draft, setDraft] = React.useState("");
  // PM subject line (editable, seeded from the thread title). Comment mode unused.
  const [subject, setSubject] = React.useState(() => defaultSubject(opp));
  const [generating, setGenerating] = React.useState(false);
  const [posting, setPosting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [posted, setPosted] = React.useState(null); // { permalink }
  const [connecting, setConnecting] = React.useState(false);
  // A reply posted to this opportunity in a previous session: { permalink }.
  const [priorReply, setPriorReply] = React.useState(null);
  // Selected tone — a built-in key ("helpful") or a custom tone id (uuid).
  const [tone, setTone] = React.useState("helpful");
  // The user's custom tones, fetched on open (built-ins are static).
  const [customTones, setCustomTones] = React.useState([]);
  // Edit the raw Markdown, or preview how Reddit will render it.
  const [view, setView] = React.useState("edit");
  // Product link pulled from the active profile (drives the CTA affordance).
  const [productLink, setProductLink] = React.useState(null); // { name, host, url }
  const startedRef = React.useRef(false);
  // Hard guard: at most ONE generation request may be in flight at any time.
  // Every generation is a billable model call, so this makes a double-fire
  // (rapid clicks, effect re-runs, races) impossible rather than just unlikely.
  const inFlightRef = React.useRef(false);
  const toneRef = React.useRef(tone);
  React.useEffect(() => { toneRef.current = tone; }, [tone]);
  const customTonesRef = React.useRef(customTones);
  React.useEffect(() => { customTonesRef.current = customTones; }, [customTones]);

  // Built-ins + the user's custom tones, in one ordered list for the chip row.
  const tones = React.useMemo(
    () => [...BUILTIN_TONES, ...customTones.map(customToneEntry)],
    [customTones],
  );

  const parentThingId = parentThingIdFor(opp);

  // Pull the profile's product link once so the CTA tone can show what it'll
  // embed (and warn when there's nothing to link). Best-effort, non-blocking.
  React.useEffect(() => {
    if (!profileId || !window.api?.profile?.get) return;
    let cancelled = false;
    window.api.profile.get(profileId).then((resp) => {
      const p = resp?.profile;
      if (cancelled || !p) return;
      const host = prettyHost(p.website);
      setProductLink({
        name: (p.product_name || "").trim(),
        host,
        url: host ? (p.website.startsWith("http") ? p.website : `https://${p.website}`) : "",
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [profileId]);

  // Build the opportunity context the generator needs.
  const opportunityContext = React.useMemo(() => ({
    subreddit: opp.subreddit,
    kind: opp.kind,
    title: opp.title,
    redditTitle: opp.redditTitle,
    excerpt: opp.excerpt,
    threadContext: opp.threadContext,
    why: opp.why,
    angle: opp.angle,
    persona: opp.persona,
    pain: opp.pain,
    useCase: opp.useCase,
    intent: opp.intent,
  }), [opp]);

  const generate = React.useCallback(async (toneOverride) => {
    // Drop any call while a request is already running — never double-bill.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const tid = toneOverride || toneRef.current;
    const isCustom = customTonesRef.current.some((t) => t.id === tid);
    setError("");
    setView("edit");
    setGenerating(true);
    try {
      const resp = isPM
        ? await window.api.reddit.generatePM({
            profileId,
            opportunity: opportunityContext,
            author: recipient,
            tone: isCustom ? undefined : tid,
            toneId: isCustom ? tid : undefined,
          })
        : await window.api.reddit.generateReply({
            profileId,
            opportunity: opportunityContext,
            tone: isCustom ? undefined : tid,
            toneId: isCustom ? tid : undefined,
          });
      setDraft(resp?.draft || "");
    } catch (e) {
      setError(e?.message || "Could not generate a response.");
    } finally {
      setGenerating(false);
      inFlightRef.current = false;
    }
  }, [profileId, opportunityContext, isPM, recipient]);

  // Pick a tone and immediately redraft in that voice (when already connected).
  const pickTone = React.useCallback((nextTone) => {
    if (nextTone === toneRef.current || generating || posting) return;
    setTone(nextTone);
    if (connection?.connected) generate(nextTone);
  }, [generate, generating, posting, connection]);

  // Jump to Settings to manage tones; close the dialog first.
  const editTones = React.useCallback(() => {
    onClose();
    onNavigate?.("settings");
  }, [onClose, onNavigate]);

  // On open: check connection, resolve the user's DEFAULT tone, and draft
  // EXACTLY ONE reply in that tone. The order matters for both billing and UX:
  //   1. We stay in the "Checking…" state until the settings default is known,
  //      so the composer never first appears on "Helpful" and visibly jumps to
  //      the real default.
  //   2. We only ever call generate() once here (startedRef), and generate()
  //      itself refuses to overlap (inFlightRef) — so opening the dialog costs
  //      exactly one billable generation, always in the intended voice.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await window.api.reddit.connection(userId);
        const conn = resp?.connection || { connected: false };
        if (cancelled) return;

        // Not connected (or we've already kicked off): just reveal the screen.
        if (!conn.connected || startedRef.current) {
          setConnection(conn);
          return;
        }
        startedRef.current = true;

        // Resolve tones + default BEFORE revealing the composer or drafting,
        // so the very first (and only) request uses the settings default —
        // never a throwaway "helpful" pass.
        let initialTone = "helpful";
        try {
          const tr = await window.api.reddit.replyTones.list(userId);
          if (cancelled) return;
          const custom = tr?.tones || [];
          setCustomTones(custom);
          customTonesRef.current = custom;
          initialTone = tr?.default || "helpful";
          setTone(initialTone);
          toneRef.current = initialTone;
        } catch (_) { /* fall back to built-in default */ }
        if (cancelled) return;

        // Now reveal the composer (already on the right tone) and draft once.
        setConnection(conn);
        // Prior-reply warning is comment-only (PMs have no posted-status concept).
        if (!isPM && opp.opportunityId) {
          window.api.reddit.postedStatus(opp.opportunityId).then((r) => {
            if (!cancelled && r?.posted) {
              setPriorReply({ permalink: r.permalink || null });
            }
          }).catch(() => {});
        }
        generate(initialTone);
      } catch (e) {
        if (!cancelled) setConnection({ connected: false });
      }
    })();
    return () => { cancelled = true; };
  }, [userId, generate]);

  // Escape closes the dialog (but not while posting).
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !posting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, posting]);

  const onConnect = async () => {
    if (!userId) return;
    setConnecting(true);
    try {
      const resp = await window.api.reddit.startOAuth(userId, "/settings");
      if (resp?.url) window.location.assign(resp.url);
    } catch (e) {
      setConnecting(false);
      setError(e?.message || "Could not start the Reddit connection.");
    }
  };

  const doPost = async (force) => {
    setError("");
    setPosting(true);
    try {
      const resp = await window.api.reddit.postReply({
        userId,
        parentThingId,
        text: draft,
        opportunityId: opp.opportunityId || null,
        subreddit: opp.subreddit || null,
        force: !!force,
      });
      if (resp?.ok) {
        setPosted({ permalink: resp.permalink || null });
        return;
      }
      // Double-post guard: offer to confirm and re-post with force.
      if (resp?.code === "duplicate_post") {
        const again = window.confirm(
          "You've already posted a reply to this opportunity. Post another reply anyway?"
        );
        if (again) {
          setPosting(false);
          return doPost(true);
        }
        setError("");
        return;
      }
      setError(resp?.error || "Could not post the reply.");
    } catch (e) {
      setError(e?.message || "Could not post the reply.");
    } finally {
      setPosting(false);
    }
  };

  // Send the edited private message to the author. No duplicate guard — Reddit
  // allows repeated DMs and there's no posted-status to dedupe against.
  const doSend = async () => {
    setError("");
    setPosting(true);
    try {
      const resp = await window.api.reddit.sendPM({
        userId,
        recipient,
        subject: subject.trim() || defaultSubject(opp),
        body: draft,
      });
      if (resp?.ok) {
        setPosted({ permalink: null });
        return;
      }
      setError(resp?.error || "Could not send the message.");
    } catch (e) {
      setError(e?.message || "Could not send the message.");
    } finally {
      setPosting(false);
    }
  };

  // Single submit entry point so the action button doesn't branch inline.
  const submit = () => (isPM ? doSend() : doPost(false));

  const connected = connection?.connected;
  const checking = connection === null;
  // Comment needs a reply target; PM needs a recipient.
  const targetOk = isPM ? !!recipient : !!parentThingId;
  const canPost = connected && !!draft.trim() && targetOk && !posting && !generating && !posted;

  return (
    <>
      {/* Overlay sits above the detail panel (z-index 70) so the panel is
          blurred behind this dialog, not in front of it. Fixed positioning
          covers the whole viewport on both desktop and mobile. */}
      <div
        className="detail-overlay is-open"
        style={{ position: "fixed", zIndex: 200 }}
        onClick={() => { if (!posting) onClose(); }}
      />
      <div
        role="dialog"
        aria-label={isPM ? "Generate Reddit message" : "Generate Reddit response"}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(620px, calc(100vw - 32px))",
          maxHeight: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          background: "var(--bg-elevated, var(--bg))",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-xl, 16px)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          zIndex: 201,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "16px 18px", borderBottom: "1px solid var(--border-subtle)",
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>
            {isPM
              ? `Send a private message${recipient ? ` to u/${recipient}` : ""}`
              : "Generate Reddit response"}
          </div>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => { if (!posting) onClose(); }}
            title="Close"
            disabled={posting}
          >
            <window.I.X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="scroll" style={{ padding: 18, overflowY: "auto" }}>
          {checking && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-secondary)", fontSize: 13 }}>
              <window.I.Sparkles size={14} style={{ color: "var(--accent)", animation: "spin 900ms linear infinite" }} />
              Checking your Reddit connection…
            </div>
          )}

          {!checking && !connected && (
            <div>
              <div style={{
                display: "flex", gap: 12, alignItems: "flex-start",
                padding: "14px 16px",
                background: "var(--accent-subtle)",
                border: "1px solid var(--accent-border)",
                borderRadius: "var(--r-lg, 12px)",
              }}>
                <window.I.Reddit size={20} style={{ color: "var(--accent-soft-text)", flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>
                    {isPM
                      ? "Connect Reddit to generate and send messages"
                      : "Connect Reddit to generate and post replies"}
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.5 }}>
                    Link your Reddit account first. Community Ninja will only
                    {isPM ? " send when you explicitly click Send message." : " post when you explicitly click Post now."}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                <button className="btn btn-secondary btn-sm" onClick={onClose}>
                  Cancel
                </button>
                {onNavigate && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => { onClose(); onNavigate("settings"); }}
                  >
                    <window.I.Settings size={13} />
                    Open Settings
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={onConnect}
                  disabled={connecting}
                >
                  {connecting ? (
                    <window.I.Refresh size={13} style={{ animation: "spin 900ms linear infinite" }} />
                  ) : (
                    <window.I.Reddit size={13} />
                  )}
                  Connect Reddit
                </button>
              </div>
            </div>
          )}

          {!checking && connected && posted && (
            <div>
              <div style={{
                display: "flex", gap: 12, alignItems: "flex-start",
                padding: "14px 16px",
                background: "var(--success-subtle)",
                border: "1px solid var(--success-border)",
                borderRadius: "var(--r-lg, 12px)",
              }}>
                <window.I.CheckCircle size={20} style={{ color: "var(--success-text)", flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>
                    {isPM ? "Message sent" : "Reply posted to Reddit"}
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.5 }}>
                    {isPM
                      ? `Your message to u/${recipient} was sent as u/${connection?.reddit_username || "you"}.`
                      : `Your reply is live as u/${connection?.reddit_username || "you"}.`}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                {posted.permalink && (
                  <a
                    className="btn btn-secondary btn-sm"
                    href={posted.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <window.I.External size={13} />
                    View on Reddit
                  </a>
                )}
                <button className="btn btn-primary btn-sm" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}

          {!checking && connected && !posted && (
            <>
              <div style={{ fontSize: 12.5, color: "var(--text-tertiary)", marginBottom: 12 }}>
                {isPM ? "Sending as " : "Posting as "}
                <strong style={{ color: "var(--text-secondary)" }}>u/{connection?.reddit_username || "you"}</strong>
                {isPM ? " · review and edit before sending." : " · review and edit before posting."}
              </div>

              {isPM && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{
                    fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)",
                    textTransform: "uppercase", letterSpacing: "0.05em",
                    display: "block", marginBottom: 6,
                  }}>
                    Subject
                  </label>
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value.slice(0, PM_SUBJECT_MAX))}
                    placeholder="Message subject"
                    maxLength={PM_SUBJECT_MAX}
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      fontSize: 13.5,
                      color: "var(--text-primary)",
                      background: "var(--bg-sunken)",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                  />
                </div>
              )}

              {priorReply && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  marginBottom: 12, padding: "8px 10px",
                  background: "var(--warning-subtle, color-mix(in srgb, var(--warning, #f59e0b) 12%, transparent))",
                  border: "1px solid var(--warning-border, color-mix(in srgb, var(--warning, #f59e0b) 35%, transparent))",
                  borderRadius: 8, fontSize: 12.5, color: "var(--text-secondary)",
                }}>
                  <window.I.Info size={13} style={{ flexShrink: 0 }} />
                  <span>You've already replied to this opportunity.</span>
                  {priorReply.permalink && (
                    <a
                      href={priorReply.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--accent-soft-text)", fontWeight: 600, marginLeft: "auto", whiteSpace: "nowrap" }}
                    >
                      View your reply →
                    </a>
                  )}
                </div>
              )}

              {/* Tone selector — drives the voice and whether the product link
                  gets woven in. Picking a tone redrafts in that voice. */}
              <ToneSelector
                tones={tones}
                tone={tone}
                onPick={pickTone}
                onEdit={editTones}
                disabled={generating || posting}
                productLink={productLink}
              />

              {/* Editor / preview. Reddit renders Markdown, so the preview
                  shows links, bold, etc. the way the comment will actually look. */}
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                marginTop: 16, marginBottom: 8,
              }}>
                <div style={{ display: "flex", gap: 2, background: "var(--bg-sunken)", border: "1px solid var(--border-subtle)", borderRadius: 8, padding: 2 }}>
                  {[["edit", "Write", "Edit"], ["preview", "Preview", "Eye"]].map(([id, label, icon]) => {
                    const Cmp = window.I[icon];
                    const on = view === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setView(id)}
                        disabled={generating}
                        style={{
                          display: "flex", alignItems: "center", gap: 5,
                          padding: "5px 11px", fontSize: 12, fontWeight: 600,
                          border: "none", borderRadius: 6, cursor: generating ? "default" : "pointer",
                          background: on ? "var(--bg-elevated, var(--bg))" : "transparent",
                          color: on ? "var(--text-primary)" : "var(--text-tertiary)",
                          boxShadow: on ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                        }}
                      >
                        <Cmp size={12} />
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 11.5, color: "var(--text-quaternary)", display: "flex", alignItems: "center", gap: 4 }}>
                  <window.I.Info size={11} /> Markdown supported
                </span>
              </div>

              {generating ? (
                <div style={{
                  minHeight: 200,
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  gap: 10, color: "var(--text-secondary)", fontSize: 13,
                  border: "1px dashed var(--border)",
                  borderRadius: "var(--r-lg, 12px)",
                }}>
                  <window.I.Sparkles size={20} style={{ color: "var(--accent)", animation: "spin 900ms linear infinite" }} />
                  Drafting a {(tones.find((t) => t.id === tone) || {}).label?.toLowerCase() || "helpful"} {isPM ? "message…" : "reply…"}
                </div>
              ) : view === "preview" ? (
                <div style={{
                  minHeight: 200,
                  padding: "12px 14px",
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  color: "var(--text-primary)",
                  background: "var(--bg-sunken)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--r-lg, 12px)",
                  overflowWrap: "anywhere",
                }}>
                  <MarkdownPreview text={draft} />
                </div>
              ) : (
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={isPM ? "Your message draft will appear here…" : "Your reply draft will appear here…"}
                  rows={9}
                  style={{
                    width: "100%",
                    resize: "vertical",
                    minHeight: 200,
                    padding: "12px 14px",
                    fontSize: 13.5,
                    lineHeight: 1.55,
                    color: "var(--text-primary)",
                    background: "var(--bg-sunken)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--r-lg, 12px)",
                    outline: "none",
                    fontFamily: "inherit",
                  }}
                />
              )}

              {!generating && draft.trim() && (
                <div style={{ fontSize: 11, color: "var(--text-quaternary)", marginTop: 6, textAlign: "right" }}>
                  {draft.trim().split(/\s+/).length} words · {draft.length} chars
                </div>
              )}

              {!targetOk && !generating && (
                <div style={{ fontSize: 12, color: "var(--warning, #b45309)", marginTop: 8 }}>
                  {isPM
                    ? "This item has no known author to message, so sending is disabled. You can still copy the draft."
                    : "Couldn't determine the Reddit thread to reply to for this item, so posting is disabled. You can still copy the draft."}
                </div>
              )}

              {error && (
                <div style={{
                  marginTop: 12,
                  padding: "8px 10px",
                  background: "var(--danger-subtle)",
                  color: "var(--danger-text)",
                  border: "1px solid var(--danger-border)",
                  borderRadius: 6,
                  fontSize: 12.5,
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center" }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={onClose}
                  disabled={posting}
                >
                  Cancel
                </button>
                <div style={{ flex: 1 }} />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => generate()}
                  disabled={generating || posting}
                >
                  <window.I.Refresh size={13} style={generating ? { animation: "spin 900ms linear infinite" } : null} />
                  Regenerate
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={submit}
                  disabled={!canPost}
                  title={
                    !targetOk
                      ? (isPM ? "No author to message for this item" : "No reply target for this item")
                      : (isPM ? `Send this private message to u/${recipient}` : "Post this reply to Reddit")
                  }
                >
                  {posting ? (
                    <window.I.Sparkles size={13} style={{ color: "var(--accent)", animation: "spin 900ms linear infinite" }} />
                  ) : (
                    isPM ? <window.I.Mail size={13} /> : <></>
                  )}
                  {posting
                    ? (isPM ? "Sending…" : "Posting…")
                    : (isPM ? "Send message" : "Post reply")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* Tone chips + the active tone's hint, plus the product-link affordance for
   the tones that lean on it (CTA / soft mention). */
function ToneSelector({ tones, tone, onPick, onEdit, disabled, productLink }) {
  const active = tones.find((t) => t.id === tone) || tones[0];
  const hasLink = !!productLink?.host;

  return (
    <div>
      <div style={{
        display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)",
          textTransform: "uppercase", letterSpacing: "0.05em",
        }}>
          Tone
        </span>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            style={{
              marginLeft: "auto", display: "flex", alignItems: "center", gap: 4,
              background: "none", border: "none", padding: 0, cursor: "pointer",
              fontSize: 11.5, fontWeight: 600, color: "var(--accent-soft-text, var(--accent))",
            }}
            title="Create and manage your reply tones in Settings"
          >
            <window.I.Edit size={11} />
            Edit tones
          </button>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {tones.map((t) => {
          const Cmp = window.I[t.icon] || window.I.Sparkles;
          const on = t.id === tone;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onPick(t.id)}
              disabled={disabled}
              title={t.hint}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 12px", fontSize: 12.5, fontWeight: 600,
                borderRadius: 999, cursor: disabled ? "default" : "pointer",
                border: "1px solid " + (on ? "var(--accent)" : "var(--border)"),
                background: on ? "var(--accent-subtle)" : "var(--bg-sunken)",
                color: on ? "var(--accent-soft-text, var(--accent))" : "var(--text-secondary)",
                opacity: disabled && !on ? 0.6 : 1,
                transition: "all 120ms ease",
                maxWidth: 220,
              }}
            >
              <Cmp size={13} style={{ flexShrink: 0 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.label}
              </span>
              {t.link && (
                <window.I.External
                  size={11}
                  style={{ opacity: hasLink ? 0.9 : 0.4, flexShrink: 0 }}
                />
              )}
            </button>
          );
        })}
      </div>

      <div style={{
        marginTop: 8, fontSize: 12, color: "var(--text-tertiary)",
        lineHeight: 1.5, display: "flex", alignItems: "center", gap: 6,
      }}>
        <span>{active.hint}</span>
      </div>

      {/* Product-link affordance: only for tones that use it. */}
      {active.link && (
        hasLink ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            marginTop: 8, padding: "6px 10px",
            background: "var(--accent-subtle)",
            border: "1px solid var(--accent-border)",
            borderRadius: 8, fontSize: 12, color: "var(--text-secondary)",
          }}>
            <window.I.Zap size={12} style={{ color: "var(--accent)", flexShrink: 0 }} />
            <span>
              Will embed your link:{" "}
              <a
                href={productLink.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--accent-soft-text, var(--accent))", fontWeight: 600 }}
              >
                {productLink.host}
              </a>
            </span>
          </div>
        ) : (
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            marginTop: 8, padding: "6px 10px",
            background: "var(--bg-sunken)",
            border: "1px dashed var(--border)",
            borderRadius: 8, fontSize: 12, color: "var(--text-tertiary)",
          }}>
            <window.I.Info size={12} style={{ flexShrink: 0 }} />
            <span>
              No website on your profile — your product will be named, but not linked.
            </span>
          </div>
        )
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Single-opp body — original detail layout. Used for posts and for any   */
/* solo comment (a thread with only one matching comment).                 */
/* ────────────────────────────────────────────────────────────────────── */

function SoloBody({ opp, onFeedback, onDeleteOpportunity }) {
  const o = opp;
  const headline = o.title;
  const deleteId = o.opportunityId || null;
  const rawRedditTitle = o.redditTitle || null;
  const showRawTitle =
    !!rawRedditTitle && rawRedditTitle.trim() !== headline.trim();

  return (
    <>
      <div className="detail-section detail-headline">
        <div className="detail-headline-meta">
          <span className="opp-brief-sub">{o.subreddit}</span>
          <span className="opp-brief-sep">·</span>
          <window.KindPill kind={o.kind} />
          <span className="opp-brief-sep">·</span>
          <span className="detail-headline-meta-item"><window.I.Clock size={11} /> {o.posted}</span>
          <span className="opp-brief-sep">·</span>
          <span className="detail-headline-meta-item"><window.I.Comment size={11} /> {o.comments} comments</span>
          <span className="opp-brief-sep">·</span>
          <span className="detail-headline-meta-item"><window.I.Up size={11} /> {o.upvotes} upvotes</span>
        </div>
        <h3 className="detail-headline-title">{headline}</h3>
        {showRawTitle && (() => {
          const url = threadPermalinkUrl(o.permalink);
          return (
            <div style={{
              marginTop: 6, fontSize: 14, color: "var(--text-tertiary)",
            }}>
              Source thread:{" "}
              <a
                href={url || "#"}
                target={url ? "_blank" : undefined}
                rel={url ? "noopener noreferrer" : undefined}
                onClick={(e) => { if (!url) e.preventDefault(); }}
                aria-disabled={!url}
                title={url ? "Open source thread on Reddit" : "No permalink for this thread"}
                style={{
                  color: "#d65d26",
                  fontWeight: 500,
                  textDecoration: url ? "none" : "none",
                  cursor: url ? "pointer" : "default",
                  opacity: url ? 1 : 0.7,
                }}
              >
                {rawRedditTitle}
              </a>
            </div>
          );
        })()}
      </div>

      <div className="detail-section">
        <div className="detail-section-label">Original {o.kind}</div>
        <div className="thread-excerpt">
          <span className="quote-mark">“</span>
          <div style={{ paddingLeft: 14 }}>{o.excerpt || "(no body text)"}</div>
        </div>
      </div>

      <OpportunityMetadataBlock opp={o} />

      <div className="detail-section">
        <div className="detail-section-label">Feedback</div>
        <FeedbackGrid opp={o} onFeedback={onFeedback} />
        <div style={{ fontSize: 11.5, color: "var(--text-quaternary)", marginTop: 8 }}>
          Your feedback tunes future scans for this profile.
        </div>
        {deleteId && (
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            style={{
              marginTop: 12,
              color: "var(--danger-text)",
              borderColor: "var(--danger-border)",
            }}
            onClick={() => {
              if (!onDeleteOpportunity) return;
              if (!window.confirm("Delete this opportunity from the inbox? This also removes its saved feedback history for this profile.")) {
                return;
              }
              onDeleteOpportunity(deleteId);
            }}
          >
            <window.I.Trash size={13} />
            Delete
          </button>
        )}
      </div>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Cluster body — one thread header, then one full-metadata section per    */
/* clustered comment with its own feedback row + "Open comment" link.      */
/* ────────────────────────────────────────────────────────────────────── */

function ClusterBody({ opp, onFeedback, onDeleteOpportunity }) {
  const members = Array.isArray(opp.members) ? opp.members : [];
  const threadTitle = opp.threadTitle || opp.title;
  // The cluster's permalink is taken from the top member, which for a
  // comment points at the comment's anchor — we want the thread root for
  // the header link. Strip the comment id suffix; Reddit redirects either
  // way but this is cleaner.
  const threadUrl = threadPermalinkUrl(opp.permalink);

  return (
    <>
      {/* Thread header */}
      <div className="detail-section detail-headline">
        <div className="detail-headline-meta">
          <span className="opp-brief-sub">{opp.subreddit}</span>
          <span className="opp-brief-sep">·</span>
          <span
            className="pill pill-neutral"
            style={{ textTransform: "none" }}
          >
            <window.I.Comment size={11} style={{ color: "var(--text-tertiary)" }} />
            {members.length} comments matched
          </span>
          <span className="opp-brief-sep">·</span>
          <span className="detail-headline-meta-item">
            <window.I.Clock size={11} /> {opp.posted}
          </span>
        </div>
        <h3 className="detail-headline-title">
          <span style={{ color: "var(--text-tertiary)", fontWeight: 500 }}>
            Comments on:{" "}
          </span>
          {threadTitle}
        </h3>
        <div style={{
          marginTop: 8,
          fontSize: 12.5,
          color: "var(--text-tertiary)",
          lineHeight: 1.5,
        }}>
          {members.length} commenters in this thread match your profile.
          Review each one and react individually — feedback is per-comment.
          {threadUrl && (
            <>
              {" "}
              <a
                href={threadUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--accent)", textDecoration: "none" }}
              >
                Open the full thread →
              </a>
            </>
          )}
        </div>
      </div>

      {/* One section per clustered comment */}
      {members.map((member, idx) => (
        <ClusterMemberSection
          key={member.id}
          member={member}
          index={idx}
          total={members.length}
          onFeedback={onFeedback}
          onDeleteOpportunity={onDeleteOpportunity}
        />
      ))}
    </>
  );
}

function ClusterMemberSection({
  member, index, total, onFeedback, onDeleteOpportunity,
}) {
  const m = member;
  const deleteId = m.opportunityId || null;
  const commentUrl = permalinkUrl(m.permalink);
  return (
    <div
      style={{
        marginTop: 24,
        paddingTop: 20,
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      {/* Section divider header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 14, flexWrap: "wrap",
      }}>
        <span
          className="pill pill-neutral"
          style={{ textTransform: "none", fontWeight: 500 }}
        >
          Comment {index + 1} of {total}
        </span>

        <span className="opp-brief-sep">·</span>
        <span className={"opp-brief-tag " + tierClassOf(m.tier)}>
          {m.score}% match
        </span>
        {m.isNew && (
          <span className="opp-new-dot" title="New since last scan" />
        )}
      </div>

      {/* Headline (LLM-written) */}
      <h4 style={{
        margin: "0 0 10px 0",
        fontSize: 16,
        fontWeight: 600,
        color: "var(--text-primary)",
        lineHeight: 1.35,
      }}>
        {m.title}
      </h4>

      {/* Original comment text */}
      <div className="detail-section" style={{ marginTop: 6 }}>
        <div className="detail-section-label">Original comment</div>
        <div className="thread-excerpt">
          <span className="quote-mark">“</span>
          <div style={{ paddingLeft: 14 }}>{m.excerpt || "(no body text)"}</div>
        </div>
      </div>

      <OpportunityMetadataBlock opp={m} />

      <div className="detail-section">
        <div className="detail-section-label">Feedback</div>
        <FeedbackGrid opp={m} onFeedback={onFeedback} />
        {deleteId && (
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            style={{
              marginTop: 12,
              color: "var(--danger-text)",
              borderColor: "var(--danger-border)",
            }}
            onClick={() => {
              if (!onDeleteOpportunity) return;
              if (!window.confirm("Delete this opportunity from the inbox? This also removes its saved feedback history for this profile.")) {
                return;
              }
              onDeleteOpportunity(deleteId);
            }}
          >
            <window.I.Trash size={13} />
            Delete
          </button>
        )}
      </div>

      {/* Per-section permalink */}
      {commentUrl && (
        <a
          className="btn btn-secondary btn-sm"
          href={commentUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginTop: 8 }}
        >
          <window.I.External size={13} />
          Open comment on Reddit
        </a>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Shared metadata blocks                                                  */
/* ────────────────────────────────────────────────────────────────────── */

function OpportunityMetadataBlock({ opp }) {
  const o = opp;
  return (
    <>
      <div className="detail-section">
        <div className="detail-section-label">Why this is relevant</div>
        <div style={{
          fontSize: 13.5, color: "var(--text-primary)",
          lineHeight: 1.6, fontWeight: 500,
        }}>
          {o.why}
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr",
          gap: 10, marginTop: 14,
        }}>
          <DetailMetaCard
            label="Matched customer"
            value={o.persona && o.persona !== "—" ? o.persona : "—"}
            icon="User"
          />
          <DetailMetaCard
            label="Use case"
            value={o.useCase && o.useCase !== "—" ? o.useCase : "—"}
            icon="Target"
          />
          <DetailMetaCard
            label="Pain point"
            value={o.pain && o.pain !== "—" ? o.pain : "—"}
            icon="Alert"
          />
          <DetailMetaCard
            label="Intent"
            value={o.intent || "Discussion"}
            icon="Sparkles"
          />
        </div>
      </div>

      <div className="detail-section">
        <div className="detail-section-label">Suggested helpful response angle</div>
        <div style={{
          fontSize: 13, color: "var(--text-primary)",
          background: "var(--accent-subtle)",
          border: "1px solid var(--accent-border)",
          borderRadius: "var(--r-lg)",
          padding: "12px 14px",
          lineHeight: 1.6,
        }}>
          {o.angle}
        </div>
      </div>

      {o.caution && (
        <div className="detail-section">
          <div className="detail-section-label">Before you reply</div>
          <ul className="warning-list">
            <li className="warning-item" style={{ fontWeight: 500 }}>
              <window.I.Alert size={14} /> {o.caution}
            </li>
          </ul>
        </div>
      )}
    </>
  );
}

function FeedbackGrid({ opp, onFeedback }) {
  return (
    <div className="feedback-grid">
      {FB_REASONS.map((r) => {
        const Cmp = window.I[r.icon];
        const isPositive = r.id === "relevant";
        const active = opp.feedback === r.id;
        return (
          <button
            key={r.id}
            className={
              "feedback-btn " +
              (active ? (isPositive ? "is-active positive" : "is-active negative") : "")
            }
            onClick={() => onFeedback(opp.id, active ? null : r.id)}
            style={{ justifyContent: "flex-start", padding: "8px 12px" }}
          >
            <Cmp size={13} />
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function DetailMetaCard({ label, value, icon }) {
  const Cmp = window.I[icon];
  return (
    <div style={{
      padding: "10px 12px",
      background: "var(--bg-sunken)",
      border: "1px solid var(--border-subtle)",
      borderRadius: "var(--r-md)",
    }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
        <Cmp size={11} />
        {label}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.4 }}>{value}</div>
    </div>
  );
}

window.DetailPanel = DetailPanel;
window.GenerateResponseDialog = GenerateResponseDialog;
